/**
 * 366 (#1223, design 1c): typing help for the identity number — dashes in a MyKad while typing, the birth date read
 * back from it, and the same refusal sentences the server sends. The backend (`users/identityNumber.ts`) is the
 * authority; this only tells the customer early.
 */

export type IdentityKind = 'mykad' | 'passport';

export interface IdentityNumberValue {
  kind: IdentityKind;
  country?: string;
  number: string;
}

export const INVALID_MYKAD = "This isn't a valid MyKad number.";
export const INVALID_PASSPORT =
  "Enter the passport's country and number (5 to 20 letters or digits).";

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function mykadDigits(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 12);
}

/** `900101011234` → `900101-01-1234`, at any length while typing. */
export function formatMykad(raw: string): string {
  const d = mykadDigits(raw);
  return [d.slice(0, 6), d.slice(6, 8), d.slice(8, 12)].filter(Boolean).join('-');
}

function realDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1) return false;
  const t = new Date(Date.UTC(year, month - 1, day));
  return t.getUTCFullYear() === year && t.getUTCMonth() === month - 1 && t.getUTCDate() === day;
}

/** "Born 1 January 1990": 20YY when YY is not after this year's, else 19YY (the server's rule). */
export function birthDateText(raw: string, now: Date = new Date()): string | null {
  const d = mykadDigits(raw);
  if (d.length < 6) return null;
  const yy = Number(d.slice(0, 2));
  const month = Number(d.slice(2, 4));
  const day = Number(d.slice(4, 6));
  const year = yy <= now.getUTCFullYear() % 100 ? 2000 + yy : 1900 + yy;
  return realDate(year, month, day) ? `Born ${day} ${MONTHS[month - 1]} ${year}` : null;
}

/** The sentence to show before sending, or null when the server would accept it. */
export function identityNumberError(value: IdentityNumberValue): string | null {
  if (value.kind === 'mykad') {
    const d = value.number.replace(/[\s-]/g, '');
    if (!/^\d{12}$/.test(d)) return INVALID_MYKAD;
    const yy = Number(d.slice(0, 2));
    const month = Number(d.slice(2, 4));
    const day = Number(d.slice(4, 6));
    return realDate(1900 + yy, month, day) || realDate(2000 + yy, month, day)
      ? null
      : INVALID_MYKAD;
  }
  const number = value.number.replace(/[\s-]/g, '').toUpperCase();
  const country = (value.country ?? '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(country) && /^[A-Z0-9]{5,20}$/.test(number) ? null : INVALID_PASSPORT;
}

/** The body the API takes (contracts `IdentityNumberInput`). */
export function identityNumberBody(value: IdentityNumberValue) {
  return value.kind === 'mykad'
    ? { kind: 'mykad' as const, number: value.number }
    : { kind: 'passport' as const, country: value.country ?? '', number: value.number };
}
